import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, RotateCcw, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useMeta, pkr, waLink } from "@/lib/site";
import { Overline } from "@/components/Reveal";

const PROFILES = {
  breeze: { gender: "her", time: ["day", "everyday"], mood: ["fresh", "floral"] },
  elysian: { gender: "her", time: ["evening", "everyday"], mood: ["warm", "floral"] },
  diva: { gender: "her", time: ["evening"], mood: ["floral", "deep"] },
  elite: { gender: "him", time: ["day", "everyday"], mood: ["fresh"] },
  legacy: { gender: "him", time: ["everyday", "evening"], mood: ["deep", "warm"] },
  "smoked-timber": { gender: "him", time: ["evening"], mood: ["smoky", "deep"] },
  pasha: { gender: "him", time: ["evening"], mood: ["deep", "warm", "smoky"] },
  "scent-luxe": { gender: "any", time: ["day", "everyday"], mood: ["fresh"] },
  "nuit-doree": { gender: "any", time: ["evening"], mood: ["warm", "deep"] },
};

const QUESTIONS = [
  { key: "gender", title: "Who will wear it?", options: [
    { value: "her", label: "For her" }, { value: "him", label: "For him" }, { value: "any", label: "For anyone" }] },
  { key: "time", title: "When will it be worn?", options: [
    { value: "day", label: "Bright daytime" }, { value: "everyday", label: "Every day, everywhere" }, { value: "evening", label: "Evenings & occasions" }] },
  { key: "mood", title: "What should it say?", options: [
    { value: "fresh", label: "Fresh & effortless" }, { value: "floral", label: "Elegant & floral" }, { value: "warm", label: "Warm & golden" }, { value: "smoky", label: "Deep & smoky" }] },
];

const pick = (answers) => {
  const scored = Object.entries(PROFILES).map(([slug, p]) => {
    let s = 0;
    if (answers.gender === "any") s += p.gender === "any" ? 3 : 1;
    else if (p.gender === answers.gender) s += 3;
    else if (p.gender === "any") s += 2;
    if (p.time.includes(answers.time)) s += 2;
    if (p.mood.includes(answers.mood)) s += 2;
    if (p.mood[0] === answers.mood) s += 1;
    return [slug, s];
  }).sort((a, b) => b[1] - a[1]);
  return [scored[0][0], scored[1][0]];
};

export default function ScentFinder() {
  useMeta("Scent Finder", "Three questions. One fragrance that will become yours. Find your Bonheur Eternal signature.");
  const { data: fragrances = [] } = useQuery({ queryKey: ["fragrances"], queryFn: async () => (await api.get("/fragrances")).data });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const answer = (key, value) => {
    setAnswers({ ...answers, [key]: value });
    setStep(step + 1);
  };
  const done = step >= QUESTIONS.length;
  const [winSlug, runnerSlug] = done ? pick(answers) : [null, null];
  const win = fragrances.find((f) => f.slug === winSlug);
  const runner = fragrances.find((f) => f.slug === runnerSlug);

  return (
    <div className="pt-32 md:pt-44 pb-32 px-5 md:px-8 max-w-4xl mx-auto min-h-screen">
      <Overline>Scent Finder</Overline>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2B2118] mt-4 tracking-tight">Three questions.<br /><span className="italic text-[#8F6B3A]">One signature.</span></h1>

      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div key={step} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="mt-16">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#6E6151]">Question {step + 1} of {QUESTIONS.length}</p>
            <h2 className="font-display text-3xl text-[#2B2118] mt-3">{QUESTIONS[step].title}</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-10">
              {QUESTIONS[step].options.map((o) => (
                <button key={o.value} data-testid={`quiz-option-${o.value}`} onClick={() => answer(QUESTIONS[step].key, o.value)}
                  className="text-left border border-[#2B2118]/[0.08] p-6 font-display text-xl text-[#2B2118] hover:border-[#8F6B3A]/60 hover:text-[#8F6B3A] transition-colors duration-300 bg-[#DDD3C1]/60">
                  {o.label}
                </button>
              ))}
            </div>
          </motion.div>
        ) : win && (
          <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="mt-16" data-testid="quiz-result">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#8F6B3A]">Your signature awaits</p>
            <div className="grid md:grid-cols-2 gap-10 mt-8 items-start">
              <Link to={`/fragrance/${win.slug}`} className="block overflow-hidden border border-[#2B2118]/[0.08] group" style={{ boxShadow: `0 0 100px -30px ${win.hex}66` }}>
                <img src={win.image} alt={win.name} className="w-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" style={{ aspectRatio: "1408/1097" }} />
              </Link>
              <div>
                <h2 className="font-display text-4xl md:text-5xl text-[#2B2118] tracking-tight" data-testid="quiz-result-name">{win.name}</h2>
                <p className="text-[#6E6151] mt-2">{win.tagline}</p>
                <p className="mt-5 text-[#5A4C3C] leading-loose">{win.description}</p>
                <p className="mt-6 font-display text-2xl text-[#8F6B3A]">{pkr(win.price)}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a href={waLink(`Hello Bonheur Eternal, your Scent Finder matched me with ${win.name} (50ml) — ${pkr(win.price)}. I'd like to order it.`)}
                    target="_blank" rel="noopener noreferrer" data-testid="quiz-whatsapp-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors">
                    <MessageCircle size={15} strokeWidth={1.5} /> Order via WhatsApp
                  </a>
                  <Link to={`/fragrance/${win.slug}`} data-testid="quiz-view-btn" className="inline-flex items-center rounded-full border border-[#2B2118]/20 px-8 py-3.5 text-xs tracking-[0.2em] uppercase text-[#2B2118] hover:border-[#8F6B3A] transition-colors">
                    View fragrance
                  </Link>
                </div>
                {runner && (
                  <p className="mt-10 text-sm text-[#6E6151]">
                    A close second — <Link to={`/fragrance/${runner.slug}`} data-testid="quiz-runner-link" className="text-[#8F6B3A] hover:underline">{runner.name}</Link>, {runner.tagline?.toLowerCase()}
                  </p>
                )}
                <div className="mt-10 pt-8 border-t border-[#2B2118]/[0.06]">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E6151] mb-4">Share your match</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`The Bonheur Eternal Scent Finder matched me with ${win.name} — "${win.tagline}" Find your signature: ${window.location.origin}/scent-finder`)}`}
                      target="_blank" rel="noopener noreferrer" data-testid="quiz-share-whatsapp"
                      className="inline-flex items-center gap-2 border border-[#2B2118]/15 px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase text-[#5A4C3C] hover:border-[#8F6B3A] hover:text-[#8F6B3A] transition-colors">
                      <Share2 size={13} strokeWidth={1.5} /> Share on WhatsApp
                    </a>
                    <button
                      data-testid="quiz-copy-link"
                      onClick={() => { navigator.clipboard.writeText(`The Bonheur Eternal Scent Finder matched me with ${win.name}. Find your signature: ${window.location.origin}/scent-finder`); toast.success("Copied — paste it anywhere, Instagram included."); }}
                      className="inline-flex items-center gap-2 border border-[#2B2118]/15 px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase text-[#5A4C3C] hover:border-[#8F6B3A] hover:text-[#8F6B3A] transition-colors">
                      <Link2 size={13} strokeWidth={1.5} /> Copy link
                    </button>
                  </div>
                </div>
                <button data-testid="quiz-restart-btn" onClick={() => { setStep(0); setAnswers({}); }} className="mt-8 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#6E6151] hover:text-[#8F6B3A] transition-colors">
                  <RotateCcw size={13} strokeWidth={1.5} /> Start over
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
